import { Button, Card, CardContent } from '@up2stay/up2promo-ui';
import { Loader2, RefreshCw } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  isOnboardingExcludedPath,
  useWorkspaceBootstrap,
} from '@/core/hooks/use-workspace-bootstrap';
import { useDictionary } from '@/core/providers/dictionary-provider';

function BootstrapSpinner() {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3"
      aria-busy="true"
      aria-live="polite"
    >
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function BootstrapError({ onRetry }: { onRetry: () => Promise<void> }) {
  const dict = useDictionary();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center space-y-4 p-8 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            <RefreshCw className="h-8 w-8 text-muted-foreground" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">
              {dict.workspace.errors.membershipsLoadTitle}
            </h2>
            <p className="text-sm text-muted-foreground">
              {dict.workspace.errors.membershipsLoadDescription}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              void handleRetry();
            }}
            disabled={isRetrying}
          >
            {isRetrying ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {dict.workspace.errors.membershipsLoadRetry}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Keep `IonTabs` mounted while memberships load. Overlay loading/error instead
 * of replacing the tab tree (unmounting tabs mid auth→shell handoff breaks
 * Ionic PageManager visibility).
 *
 * Mount only as a child of `SidebarInset` (never wrap `AppShell`): a flex-col
 * wrapper around sidebar + inset stacks them and puts pages under the sidebar.
 *
 * Overlay is `fixed` so it covers Cap tab chrome during init without a
 * body-portaled create-first CTA (DEV-470).
 *
 * `needs-onboarding` overlays the shell while bootstrap redirects to
 * `/onboarding`, except on excluded in-wizard paths (create workspace) where
 * the wizard must stay interactive through the plan step.
 */
export function WorkspaceBootstrapGate({ children }: { children: ReactNode }) {
  const { status, retry } = useWorkspaceBootstrap();
  const location = useLocation();
  const onExcludedPath = isOnboardingExcludedPath(location.pathname);

  const showOverlay =
    status === 'loading' ||
    status === 'error' ||
    (status === 'needs-onboarding' && !onExcludedPath);

  return (
    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
      {children}
      {showOverlay ? (
        <div className="fixed inset-0 z-[100] flex min-h-0 flex-col bg-background">
          {status === 'error' ? (
            <BootstrapError onRetry={retry} />
          ) : (
            <BootstrapSpinner />
          )}
        </div>
      ) : null}
    </div>
  );
}
